import { useState } from "react";
import { ArrowLeft, Building2, Mail, Phone, User, Users, MessageSquare, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";
const Consultation = () => {
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    position: "",
    employeeCount: "",
    consultationType: "",
    preferredDate: "",
    message: ""
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 폼 검증
    if (!formData.name || !formData.email || !formData.company) {
      toast({
        title: "필수 정보 누락",
        description: "이름, 이메일, 회사명은 필수 입력 항목입니다.",
        variant: "destructive"
      });
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "이메일 형식 오류",
        description: "올바른 이메일 주소를 입력해주세요.",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "상담 신청 완료",
      description: "상담 신청이 성공적으로 접수되었습니다. 영업일 기준 1-2일 내 연락드리겠습니다."
    });

    // 폼 초기화
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      position: "",
      employeeCount: "",
      consultationType: "",
      preferredDate: "",
      message: ""
    });
  };
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/")}>홈</Button>
              <Button variant="ghost" onClick={() => navigate("/about")}>소개</Button>
              <Button variant="ghost" onClick={() => navigate("/methodology")}>방법론</Button>
              <Button variant="default" className="bg-blue-500 hover:bg-blue-600">상담신청</Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 dark:bg-blue-500/20 px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">전문가 상담</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              무료 상담 신청
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              기업의 AI 리터러시 진단에 대해 전문가와 상담하세요
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-muted-foreground">24시간 내 답변</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-muted-foreground">전문가 1:1 매칭</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground">맞춤형 제안</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Side - Info Cards */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="border-2 border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      상담 프로세스
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                      <p className="text-muted-foreground">신청서 제출</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                      <p className="text-muted-foreground">담당자 배정 (1-2일)</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                      <p className="text-muted-foreground">일정 조율</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
                      <p className="text-muted-foreground">온라인/오프라인 상담</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-transparent">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-purple-500" />
                      상담 내용
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>• KAILA 프레임워크 소개</p>
                    <p>• 기업 맞춤 진단 방안</p>
                    <p>• 예상 일정 및 비용</p>
                    <p>• Q&A 세션</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-border bg-gradient-to-br from-blue-500/5 to-purple-500/5">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Phone className="h-5 w-5 text-green-500" />
                      문의
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>📧 ernestlook@gmail.com</p>
                    <p>📞 010-6745-13918</p>
                    <p>🕐 평일 10:00-17:00</p>
                  </CardContent>
                </Card>
              </div>

              {/* Right Side - Form */}
              <div className="lg:col-span-2">
                <Card className="border-2 border-border">
                  <CardHeader>
                    <CardTitle className="text-2xl">상담 신청서</CardTitle>
                    <CardDescription>
                      아래 정보를 입력해주시면 담당자가 연락드리겠습니다
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* 이름 */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          이름 <span className="text-red-500">*</span>
                        </Label>
                        <Input id="name" placeholder="홍길동" value={formData.name} onChange={e => handleChange("name", e.target.value)} required />
                      </div>

                      {/* 이메일 */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          이메일 <span className="text-red-500">*</span>
                        </Label>
                        <Input id="email" type="email" placeholder="hong@company.com" value={formData.email} onChange={e => handleChange("email", e.target.value)} required />
                      </div>

                      {/* 전화번호 */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          전화번호
                        </Label>
                        <Input id="phone" type="tel" placeholder="010-1234-5678" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} />
                      </div>

                      {/* 회사명 */}
                      <div className="space-y-2">
                        <Label htmlFor="company" className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          회사명 <span className="text-red-500">*</span>
                        </Label>
                        <Input id="company" placeholder="(주)회사명" value={formData.company} onChange={e => handleChange("company", e.target.value)} required />
                      </div>

                      {/* 직책 */}
                      <div className="space-y-2">
                        <Label htmlFor="position">직책</Label>
                        <Input id="position" placeholder="예: 인사팀 팀장" value={formData.position} onChange={e => handleChange("position", e.target.value)} />
                      </div>

                      {/* 직원 수 */}
                      <div className="space-y-2">
                        <Label htmlFor="employeeCount" className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          직원 수
                        </Label>
                        <Select value={formData.employeeCount} onValueChange={value => handleChange("employeeCount", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="선택해주세요" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-50">1-50명</SelectItem>
                            <SelectItem value="51-200">51-200명</SelectItem>
                            <SelectItem value="201-500">201-500명</SelectItem>
                            <SelectItem value="501-1000">501-1,000명</SelectItem>
                            <SelectItem value="1000+">1,000명 이상</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* 상담 유형 */}
                      <div className="space-y-2">
                        <Label htmlFor="consultationType">상담 유형</Label>
                        <Select value={formData.consultationType} onValueChange={value => handleChange("consultationType", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="선택해주세요" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="diagnosis">AI 리터러시 진단</SelectItem>
                            <SelectItem value="training">AI 교육 프로그램</SelectItem>
                            <SelectItem value="consulting">컨설팅</SelectItem>
                            <SelectItem value="partnership">파트너십</SelectItem>
                            <SelectItem value="other">기타</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* 희망 상담 일자 */}
                      <div className="space-y-2">
                        <Label htmlFor="preferredDate" className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          희망 상담 일자
                        </Label>
                        <Input id="preferredDate" type="date" value={formData.preferredDate} onChange={e => handleChange("preferredDate", e.target.value)} min={new Date().toISOString().split('T')[0]} />
                      </div>

                      {/* 문의 내용 */}
                      <div className="space-y-2">
                        <Label htmlFor="message">문의 내용</Label>
                        <Textarea id="message" placeholder="상담 받고 싶은 내용을 자유롭게 작성해주세요" rows={5} value={formData.message} onChange={e => handleChange("message", e.target.value)} />
                      </div>

                      {/* 제출 버튼 */}
                      <div className="flex gap-4">
                        <Button type="submit" size="lg" className="flex-1 bg-blue-500 hover:bg-blue-600">
                          상담 신청하기
                        </Button>
                        <Button type="button" size="lg" variant="outline" onClick={() => navigate("/")}>
                          취소
                        </Button>
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        * 표시는 필수 입력 항목입니다. 제출하신 정보는 상담 목적으로만 사용됩니다.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              {/* KAILA Info */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold">KAILA</span>
                    <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  기업의 AI 리터러시 수준을 과학적으로 진단하고<br />
                  체계적으로 개선하는 전문 프레임워크
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="font-bold mb-4">프레임워크</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    홈
                  </button>
                  <button onClick={() => navigate('/about')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    소개
                  </button>
                  <button onClick={() => navigate('/methodology')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    방법론
                  </button>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="font-bold mb-4">문의</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/consultation')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    상담 신청
                  </button>
                  <button onClick={() => navigate('/resources')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    자료 요청
                  </button>
                  <button onClick={() => navigate('/partnership')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    파트너십
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  © 2025 KAILA Framework. All rights reserved.
                </p>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <button onClick={() => navigate('/admin/login')} className="hover:text-foreground transition-colors">
                    관리자 로그인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Consultation;